// src/components/analytics/AnalyticsDashboard.tsx
/**
 * Dashboard analytics popolato con dati reali aggregati lato BE.
 * Ogni grafico e KPI è cliccabile: apre un modal con l'elenco delle richieste che lo
 * compongono, da cui si apre il dettaglio della singola richiesta in una nuova tab.
 * In fondo la sezione dedicata ai tempi di completamento (barre per richiesta, media
 * per tipologia, segmenti percentili) — vedi CompletionAnalysis.
 */

import React from 'react';
import {
  Container,
  Grid,
  Title,
  Group,
  Select,
  Button,
  Stack,
  Text,
  Card,
  Paper,
  SimpleGrid,
  Skeleton,
  Box,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useRequestAggregates } from '../../hooks/use-request-aggregates';
import { requestsNew } from '../../services/api';
import { RequestStatus, RequestType, AnalyticsRequestsQuery } from '../../types/api';
import { CompletionAnalysis } from './CompletionAnalysis';
import { RequestsDrillModal } from './RequestsDrillModal';
import {
  TYPE_META,
  STATUS_META,
  ALL_TYPES,
  ALL_STATUSES,
  typeLabel,
  statusLabel,
} from './analytics-meta';

const MONTHS_IT = [
  'gen', 'feb', 'mar', 'apr', 'mag', 'giu',
  'lug', 'ago', 'set', 'ott', 'nov', 'dic',
];

/** "2025-03" -> "mar 2025" */
function formatMonthKey(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return `${MONTHS_IT[m - 1] ?? m} ${y}`;
}

interface DrillState {
  opened: boolean;
  title: string;
  subtitle?: string;
  query: AnalyticsRequestsQuery;
}

export function AnalyticsDashboard() {
  const theme = useMantineTheme();
  const [year, setYear] = React.useState<number | undefined>(undefined);

  const [drill, setDrill] = React.useState<DrillState>({
    opened: false,
    title: '',
    query: {},
  });
  const openDrill = (title: string, query: AnalyticsRequestsQuery, subtitle?: string) =>
    setDrill({ opened: true, title, subtitle, query });
  const closeDrill = () => setDrill((d) => ({ ...d, opened: false }));

  const { data: aggregates, isLoading } = useRequestAggregates({ year });

  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await requestsNew.exportAnalytics({ year });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_ufficio_stile_${year ?? 'tutti'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notifications.show({
        title: 'Export completato',
        message: 'Il file Excel delle analytics è stato scaricato.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Errore export',
        message: 'Impossibile generare il file Excel. Riprova.',
        color: 'red',
      });
    } finally {
      setExporting(false);
    }
  };

  const yearOptions = (aggregates?.availableYears ?? []).map((y) => ({
    value: String(y),
    label: String(y),
  }));
  const yearLabel = year ? String(year) : 'tutti gli anni';

  const totalCount = aggregates?.totalCount ?? 0;
  const overallAvgDays = aggregates?.completion.overallAverageDays ?? 0;

  // Pie chart data
  const pieData = ALL_TYPES.map((t) => ({
    name: TYPE_META[t].label,
    value: aggregates?.byType[t] ?? 0,
    type: t,
    color: TYPE_META[t].color,
  })).filter((d) => d.value > 0);

  // Stacked bar Type x Status
  const stackedData = ALL_TYPES.map((type) => {
    const row: Record<string, number | string> = { name: TYPE_META[type].label, type };
    ALL_STATUSES.forEach((status) => {
      const bucket = aggregates?.byTypeAndStatus.find(
        (b) => b.type === type && b.status === status
      );
      row[STATUS_META[status].label] = bucket?.count ?? 0;
    });
    return row;
  });

  // Trend mensile per tipo (24 mesi)
  const trendData = React.useMemo(() => {
    if (!aggregates) return [];
    const map = new Map<string, Record<string, number | string>>();
    aggregates.monthlyTrend.forEach((b) => {
      const meta = TYPE_META[b.type as RequestType];
      if (!meta) return; // skip: type non mappato (es. valore enum sconosciuto)
      const key = `${b.year}-${String(b.month).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, { name: key });
      const row = map.get(key)!;
      row[meta.label] = ((row[meta.label] as number) ?? 0) + b.count;
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [aggregates]);

  // Top 10 clienti
  const topClients = (aggregates?.topClients ?? []).map((c) => ({
    name: c.clientName,
    value: c.count,
    clientId: c.clientId,
  }));

  // ---- Drill-down handlers ----
  const drillType = (type: RequestType) =>
    openDrill(typeLabel(type), { type, year }, `Anno: ${yearLabel}`);

  const drillTypeStatus = (type: RequestType, status: RequestStatus) =>
    openDrill(
      `${typeLabel(type)} · ${statusLabel(status)}`,
      { type, status, year },
      `Anno: ${yearLabel}`
    );

  const drillMonth = (monthKey: string) => {
    const [y, m] = monthKey.split('-').map(Number);
    if (!y || !m) return;
    const start = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0));
    const end = new Date(Date.UTC(y, m, 0, 23, 59, 59));
    openDrill(
      `Richieste create · ${formatMonthKey(monthKey)}`,
      { startDate: start.toISOString(), endDate: end.toISOString(), dateField: 'created' },
      'Per data di creazione'
    );
  };

  const drillClient = (clientId: number | undefined, clientName: string) =>
    openDrill(
      clientName || 'Cliente',
      { clientId: clientId ?? undefined, clientName, year },
      `Anno: ${yearLabel}`
    );

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={2}>Analytics Dashboard</Title>
            <Text c="dimmed">
              Panoramica richieste per tipologia, stato e tempi di completamento
            </Text>
          </div>
          <Group gap="sm" align="flex-end">
            <Select
              label="Anno"
              placeholder="Tutti gli anni"
              clearable
              data={yearOptions}
              value={year ? String(year) : null}
              onChange={(v) => setYear(v ? Number(v) : undefined)}
              w={180}
            />
            <Button
              leftSection={<IconFileSpreadsheet size={18} />}
              onClick={handleExport}
              loading={exporting}
              variant="light"
            >
              Esporta Excel
            </Button>
          </Group>
        </Group>

        {/* KPI Grid */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing="md">
          {ALL_TYPES.map((t) => (
            <KpiCard
              key={t}
              label={TYPE_META[t].label}
              value={aggregates?.byType[t] ?? 0}
              color={TYPE_META[t].color}
              loading={isLoading}
              onClick={() => drillType(t)}
            />
          ))}
          <KpiCard
            label="Tempo medio"
            value={`${overallAvgDays.toFixed(1)} gg`}
            color={theme.colors.gray[6]}
            loading={isLoading}
          />
          <KpiCard
            label="Totale"
            value={totalCount}
            color={theme.colors.dark[6]}
            loading={isLoading}
            onClick={() => openDrill('Tutte le richieste', { year }, `Anno: ${yearLabel}`)}
          />
        </SimpleGrid>

        <Grid>
          {/* Distribuzione per tipologia */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card withBorder p="md" h="100%">
              <Title order={4} mb="sm">
                Distribuzione per tipologia
              </Title>
              <Text size="xs" c="dimmed" mb="xs">
                Click su una fetta per le richieste
              </Text>
              {isLoading ? (
                <Skeleton height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      onClick={(data: any) => {
                        const t = (data?.type ?? data?.payload?.type) as RequestType;
                        if (t != null) drillType(t);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Grid.Col>

          {/* Tipologia x Stato */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card withBorder p="md" h="100%">
              <Title order={4} mb="sm">
                Tipologia × Stato
              </Title>
              <Text size="xs" c="dimmed" mb="xs">
                Click su una barra per le richieste di quel tipo e stato
              </Text>
              {isLoading ? (
                <Skeleton height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stackedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    {ALL_STATUSES.map((s) => (
                      <Bar
                        key={s}
                        dataKey={STATUS_META[s].label}
                        stackId="a"
                        fill={STATUS_META[s].color}
                        cursor="pointer"
                        onClick={(d: any) => {
                          const t = (d?.type ?? d?.payload?.type) as RequestType;
                          if (t != null) drillTypeStatus(t, s);
                        }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* Trend mensile */}
        <Card withBorder p="md">
          <Title order={4} mb="sm">
            Trend mensile (ultimi 24 mesi)
          </Title>
          <Text size="xs" c="dimmed" mb="xs">
            Click su un mese per le richieste create in quel periodo
          </Text>
          {isLoading ? (
            <Skeleton height={300} />
          ) : trendData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={trendData}
                onClick={(state: any) => {
                  const label = state?.activeLabel;
                  if (label) drillMonth(String(label));
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tickFormatter={formatMonthKey} />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(l) => formatMonthKey(String(l))} />
                <Legend />
                {ALL_TYPES.map((t) => (
                  <Line
                    key={t}
                    type="monotone"
                    dataKey={TYPE_META[t].label}
                    stroke={TYPE_META[t].color}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Top clienti */}
        <Card withBorder p="md">
          <Title order={4} mb="sm">
            Top 10 clienti
          </Title>
          <Text size="xs" c="dimmed" mb="xs">
            Click su una barra per le richieste del cliente
          </Text>
          {isLoading ? (
            <Skeleton height={300} />
          ) : topClients.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(300, topClients.length * 34)}>
              <BarChart data={topClients} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={120} />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill={theme.colors.blue[5]}
                  cursor="pointer"
                  onClick={(d: any) =>
                    drillClient(
                      (d?.clientId ?? d?.payload?.clientId) as number | undefined,
                      String(d?.name ?? d?.payload?.name ?? '')
                    )
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Sezione tempi di completamento: barre per richiesta, media, segmenti percentili */}
        <CompletionAnalysis />
      </Stack>

      <RequestsDrillModal
        opened={drill.opened}
        onClose={closeDrill}
        title={drill.title}
        subtitle={drill.subtitle}
        query={drill.query}
      />
    </Container>
  );
}

function KpiCard({
  label,
  value,
  color,
  loading,
  onClick,
}: {
  label: string;
  value: number | string;
  color: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  return (
    <Paper
      withBorder
      p="md"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <Stack gap={4}>
        <Text size="xs" c="dimmed" fw={500}>
          {label}
        </Text>
        {loading ? (
          <Skeleton height={28} />
        ) : (
          <Text fw={700} size="xl">
            {value}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function EmptyChart() {
  return (
    <Box py="xl" ta="center">
      <Text c="dimmed">Nessun dato disponibile per il periodo selezionato</Text>
    </Box>
  );
}
